import { useMemo } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import useGetDevourPlayUserStats from "@/hooks/useGetDevourPlayUserStats";
import { OWReward, OWUserStat, OWGameResponse } from "@devour/client";

export interface QuestWithProgress {
    quest: OWReward;
    game: OWGameResponse;
    gameId: string;
    progress: number;
    target: number;
    isClaimable: boolean;
    isClaimed: boolean;
    percentComplete: number;
    type: "daily" | "weekly" | "monthly" | "never"; // Reset period
    xpReward: number;
    fuelReward?: number;
    otherReward?: {
        type: string;
        name: string;
        amount?: number;
    };
    rewardId?: string;
    canClaim?: boolean;
    isExpired?: boolean;
    hasClaimableMilestone?: boolean; // For milestone quests with claimable milestones
    claimableMilestone?: any; // The actual milestone that can be claimed
    isSponsored?: boolean; // Whether this is a sponsored quest
    rewardCategory?: string; // EVENT or TIME category for debugging
}

/**
 * Helper function to extract reward information from a config object
 */
const extractRewardFromConfig = (config: any) => {
    const rewards = {
        xp: 0,
        fuel: 0,
        other: null as { type: string; name: string; amount?: number } | null,
    };

    if (!config) return rewards;

    if (config.type === "XP") {
        rewards.xp = config.amount || 0;
    } else if (config.type === "FUEL") {
        rewards.fuel = config.amount || 0;
    } else if (config.type) {
    // Handle any other reward type (NFT, LOOTBOX, etc.)
        rewards.other = {
            type: config.type,
            name: config.name || config.type,
            amount: config.amount || 1,
        };
    }

    return rewards;
};

/**
 * Custom hook to fetch and process all user quest stats across all games
 * This centralized hook is used by both ActiveQuestsCard and QuestProgressCard
 */
export const useUserQuestStats = (options?: {
    page?: number;
    pageSize?: number;
    daysBack?: number;
}) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    // Default values for pagination
    const page = options?.page || 1;
    const pageSize = options?.pageSize || 50; // Default to 50 for now to show most quests
    const daysBack = options?.daysBack || 365; // Default to last 365 days to show all available quests

    /*
     * Fetch ALL user stats without specifying a gameId
     * This returns all quest progress across all games with populated reward and game data
     */
    const { data, isLoading, error, refetch } = useGetDevourPlayUserStats(
        fullToken,
        undefined, // No gameId = get all stats
        !!fullToken, // Only enable if user is authenticated
        undefined, // No reward category filter
        page,
        pageSize,
        daysBack,
    );

    // Process the raw stats into our QuestWithProgress format
    const processedQuests = useMemo(() => {
        if (!data?.userStats || !Array.isArray(data.userStats)) {
            return [];
        }


        // First, filter and map the stats
        const questsWithProgress = data.userStats
            .filter(stat => {
                // Must have valid reward
                if (!stat.reward) return false;

                // Filter out Challenge quests - they have a separate UI
                if (stat.reward?.challenge) return false;

                // Filter TIME quests - only filter out the truly passive ones
                if (stat.rewardCategory === "TIME") {
                    const questName = stat.reward?.name || "";

                    // Only filter out the specific passive XP rewards
                    if (questName === "Time-based Reward" || questName.includes("XP per hour")) {
                        return false;
                    }
                    // Keep ALL other TIME quests including game-specific ones
                }

                const hasProgress = stat.progress > 0;

                // Check if quest has ANY claims in the last 7 days (for filter inclusion)
                const hasRecentClaims = (() => {
                    if (!stat.claimLogs || stat.claimLogs.length === 0) {
                        return false;
                    }
                    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
                    const hasRecent = stat.claimLogs.some((claim: any) => {
                        if (!claim || !claim.date) return false;
                        return claim.date >= sevenDaysAgo;
                    });
                    if (stat.claimLogs.length > 0 && !hasRecent) {
                    }
                    return hasRecent;
                })();

                /*
                 * Check if quest is fully claimed
                 * For milestone quests: only true if ALL milestones are claimed
                 * For regular quests: true if claimed in the last 7 days
                 */
                const isClaimed = (() => {
                    if (!stat.claimLogs || stat.claimLogs.length === 0) return false;

                    // For milestone quests, check if all milestones are claimed
                    const hasMilestones = stat.reward?.milestones && stat.reward.milestones.length > 0;
                    if (hasMilestones) {
                        const totalMilestones = stat.reward.milestones.length;
                        const claimedMilestones = stat.claimLogs.length;
                        // Only consider fully claimed if all milestones are claimed
                        return claimedMilestones >= totalMilestones;
                    }

                    // For non-milestone quests, check if claimed in the last 7 days
                    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
                    const hasRecentClaim = stat.claimLogs.some((claim: any) => {
                        if (!claim || !claim.date) return false;
                        const claimTime = claim.date;
                        return claimTime >= sevenDaysAgo;
                    });

                    return hasRecentClaim;
                })();

                // Simple expiration check - be less aggressive about filtering
                const isExpired = (() => {
                    if (isClaimed) return false;

                    // Check for reset-based expiration ONLY for daily/weekly/monthly quests
                    const resetType = stat.type || stat.quest?.resetType || stat.reward?.resetType;
                    if (resetType && resetType !== "never" && resetType !== "none") {
                        if (stat.lastStatReset && stat.updatedAt) {
                            const resetTime = new Date(stat.lastStatReset).getTime();
                            const updateTime = new Date(stat.updatedAt).getTime();
                            if (resetTime > updateTime) return true;
                        }
                    }

                    /*
                     * Don't expire quests just because they have no progress
                     * We want to show ALL available quests
                     */

                    return false;
                })();

                const isClaimable = stat.progress >= stat.target && !isClaimed;

                /*
                 * For quests with recent claims, ALWAYS show them regardless of any other state
                 * Claimed is a historical fact that doesn't care about expiration
                 */
                if (hasRecentClaims) {
                    return true;
                }

                /*
                 * Only filter out expired quests if they DON'T have claims
                 * Expired only matters for non-claimed quests
                 */
                if (isExpired) {
                    return false;
                }

                /*
                 * Always include non-expired quests - we want to show ALL available quests
                 * The filtering by progress should happen in the UI component, not here
                 */
                return !isExpired;
            })
            .map((stat: any) => {

                // Recalculate isClaimed here since it's needed for mapping
                const isClaimed = (() => {
                    if (!stat.claimLogs || stat.claimLogs.length === 0) return false;

                    // For milestone quests, check if all milestones are claimed
                    const hasMilestones = stat.reward?.milestones && stat.reward.milestones.length > 0;
                    if (hasMilestones) {
                        const totalMilestones = stat.reward.milestones.length;
                        const claimedMilestones = stat.claimLogs.length;
                        return claimedMilestones >= totalMilestones;
                    }

                    // For non-milestone quests, check if claimed in the last 7 days
                    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
                    return stat.claimLogs.some((claim: any) => {
                        if (!claim || !claim.date) return false;
                        return claim.date >= sevenDaysAgo;
                    });
                })();

                // Map the questReset values from the API - exact values are: DAILY, WEEKLY, MONTHLY, NONE
                let type: "daily" | "weekly" | "monthly" | "never" = "never";

                const questReset = stat.reward?.questReset;

                // Map reset periods based on actual API values
                if (questReset === "DAILY") {
                    type = "daily";
                } else if (questReset === "WEEKLY") {
                    type = "weekly";
                } else if (questReset === "MONTHLY") {
                    type = "monthly";
                } else if (questReset === "NONE" || !questReset) {
                    type = "never";
                }

                const progress = stat.progress || 0;
                // For milestone quests, use the overall quest target, not individual milestone targets
                const target = stat.target || stat.reward?.target || 1;
                const percentComplete = target > 0 ? Math.min(progress / target * 100, 100) : 0;

                /*
                 * For determining if a quest is expired:
                 * Use lastStatReset to check if the quest has been reset since last progress
                 * If lastStatReset > updatedAt, the quest has been reset and old progress is expired
                 */
                const isExpired = (() => {
                    // If the quest has been claimed, keep it for historical record
                    if (stat.isClaimed) return false;

                    // Check if quest has been reset (lastStatReset is more recent than updatedAt)
                    if (stat.lastStatReset && stat.updatedAt) {
                        const resetTime = new Date(stat.lastStatReset).getTime();
                        const updateTime = new Date(stat.updatedAt).getTime();

                        // If reset happened after last update, quest has expired/reset
                        if (resetTime > updateTime) {
                            return true;
                        }
                    }

                    // Additionally, for very old quests (like test quests), expire after 30 days
                    const now = Date.now();
                    const monthInMs = 30 * 24 * 60 * 60 * 1000;
                    const lastUpdate = new Date(stat.updatedAt || stat.createdAt).getTime();

                    // Check if quest name contains "Test" and is older than 30 days
                    if (stat.reward?.name?.toLowerCase().includes("test")) {
                        return now - lastUpdate > monthInMs;
                    }

                    return false;
                })();

                // Use clean claimable logic - must have reached target and not be claimed
                const isClaimable = progress >= target && !isClaimed && !isExpired;

                /*
                 * For expired claimed quests, show the historical progress
                 * For active quests, show current progress
                 */
                const effectiveProgress = progress;
                const effectivePercentComplete = percentComplete;

                // Extract rewards - now that backend populates rewardConfigs properly
                let xpReward = 0;
                let fuelReward = 0;
                let otherReward = null;

                // For regular quests, check rewardConfigs (properly populated by backend)
                if ((!stat.reward?.milestones || stat.reward.milestones.length === 0) &&
            stat.reward?.rewardConfigs && Array.isArray(stat.reward.rewardConfigs)) {
                    // Process each reward config
                    stat.reward.rewardConfigs.forEach((config: any) => {
                        const rewards = extractRewardFromConfig(config);
                        // Accumulate rewards (in case there are multiple configs)
                        xpReward += rewards.xp;
                        fuelReward += rewards.fuel;
                        if (rewards.other && !otherReward) {
                            otherReward = rewards.other;
                        }
                    });
                }
                // Fallback to questRewards if rewardConfigs not populated
                else if (stat.reward?.questRewards && Array.isArray(stat.reward.questRewards)) {
                    stat.reward.questRewards.forEach((reward: any) => {
                        if (reward.rewardType === "XP" && reward.amount) {
                            xpReward = reward.amount;
                        } else if (reward.rewardType === "FUEL" && reward.amount) {
                            fuelReward = reward.amount;
                        } else if (reward.rewardType && reward.amount) {
                            otherReward = {
                                type: reward.rewardType,
                                name: reward.rewardType,
                                amount: reward.amount,
                            };
                        }
                    });
                }

                // For milestone-based quests, find the current claimable milestone
                if (stat.reward?.milestones && stat.reward.milestones.length > 0) {

                    // Sort milestones by target to ensure order
                    const sortedMilestones = [...stat.reward.milestones].sort((a: any, b: any) => a.target - b.target);

                    // Find the highest milestone that's been reached but not yet claimed
                    let claimableMilestone = null;
                    for (const milestone of sortedMilestones) {
                        if (progress >= milestone.target) {
                            // Check if this milestone has been claimed
                            const wasClaimed = stat.claimLogs?.some((log: any) =>
                                log.target === milestone.target);
                            if (!wasClaimed) {
                                claimableMilestone = milestone;
                                break; // Take the first unclaimed milestone we've reached
                            }
                        }
                    }

                    /*
                     * If we have a claimable milestone, use its rewards
                     * Otherwise, show the next milestone's rewards
                     */
                    const milestoneToShow = claimableMilestone ||
                                  sortedMilestones.find((m: any) => m.target > progress) ||
                                  sortedMilestones[sortedMilestones.length - 1];

                    // Now that backend populates rewardConfig, extract actual rewards from milestone
                    if (milestoneToShow && milestoneToShow.rewardConfig) {
                        // Reset rewards to show only this milestone's rewards
                        const milestoneRewards = extractRewardFromConfig(milestoneToShow.rewardConfig);
                        xpReward = milestoneRewards.xp;
                        fuelReward = milestoneRewards.fuel;
                        otherReward = milestoneRewards.other;
                    }

                    /*
                     * For milestone quests, we want to track milestone claimability separately
                     * The quest itself is only "complete" when progress >= final target
                     * Store milestone claimability for later use
                     */
                    if (claimableMilestone) {
                        /*
                         * We'll handle this differently - store the claimable milestone info
                         * but don't mark the entire quest as claimable/complete
                         */
                        stat._hasClaimableMilestone = true;
                        stat._claimableMilestone = claimableMilestone;
                    }
                }

                // Also check rewardAmount field for simple rewards (fallback)
                if (!xpReward && !fuelReward && stat.reward?.rewardAmount && stat.reward?.rewardType) {
                    if (stat.reward.rewardType === "XP") {
                        xpReward = stat.reward.rewardAmount;
                    } else if (stat.reward.rewardType === "FUEL") {
                        fuelReward = stat.reward.rewardAmount;
                    }
                }

                // Check if quest has ANY claims in the last 7 days (for Claimed filter)
                const hasRecentClaims = (() => {
                    if (!stat.claimLogs || stat.claimLogs.length === 0) return false;
                    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
                    const recentClaim = stat.claimLogs.some((claim: any) => {
                        if (!claim || !claim.date) return false;
                        return claim.date >= sevenDaysAgo;
                    });


                    return recentClaim;
                })();

                return {
                    quest: stat.reward,
                    game: stat.game,
                    gameId: stat.gameId || stat.game?.id,
                    progress: effectiveProgress,
                    target,
                    isClaimable,
                    isClaimed: isClaimed, // Use the calculated isClaimed from claimLogs
                    hasRecentClaims, // Add this for the Claimed filter
                    percentComplete: effectivePercentComplete,
                    type,
                    xpReward,
                    fuelReward,
                    otherReward,
                    rewardId: stat.reward?._id || stat.reward?.id,
                    canClaim: stat.canClaim || isClaimable, // Use backend's canClaim if available
                    isExpired, // Add this flag so we can show expired status if needed
                    hasClaimableMilestone: stat._hasClaimableMilestone || false,
                    claimableMilestone: stat._claimableMilestone,
                    isSponsored: stat.reward?.isSponsored === true, // Map sponsored flag from backend
                    rewardCategory: stat.rewardCategory, // Also map reward category for debugging
                    name: stat.reward?.name || "", // Add the quest name directly
                } as QuestWithProgress;
            })
            .sort((a, b) => {
                // First: Sort by claimed status (unclaimed first)
                if (a.isClaimed !== b.isClaimed) return a.isClaimed ? 1 : -1;

                // Second: Sort by reset type (Daily, Weekly, Monthly, None)
                const resetOrder = { "daily": 1, "weekly": 2, "monthly": 3, "never": 4 };
                const aOrder = resetOrder[a.type] || 5;
                const bOrder = resetOrder[b.type] || 5;
                if (aOrder !== bOrder) return aOrder - bOrder;

                // Third: Within same reset type, sort by claimable status
                const aClaimable = a.isClaimable || a.hasClaimableMilestone;
                const bClaimable = b.isClaimable || b.hasClaimableMilestone;
                if (aClaimable !== bClaimable) return aClaimable ? -1 : 1;

                // Fourth: Sort by progress percentage
                return b.percentComplete - a.percentComplete;
            });

        // Consolidate "All Time Game Play" quests into a single entry
        const allTimeGamePlayMap = new Map<string, any>();
        const processedQuestList: any[] = [];

        questsWithProgress.forEach((quest) => {
            // Check if this is an "All Time Game Play" quest
            const questName = quest.name || quest.quest?.name || "";

            if (questName === "All Time Game Play" || questName.includes("All Time Game Play")) {
                // For All Time Game Play, we only want ONE entry total
                const existingQuest = allTimeGamePlayMap.get("all-time-gameplay");
                if (!existingQuest || quest.progress > existingQuest.progress) {
                    // Keep the one with highest progress or the first one we find
                    allTimeGamePlayMap.set("all-time-gameplay", quest);
                }
            } else {
                // Not an All Time Game Play quest, add it directly
                processedQuestList.push(quest);
            }
        });

        // Add the single consolidated All Time Game Play quest if we have any
        if (allTimeGamePlayMap.has("all-time-gameplay")) {
            const allTimeQuest = { ...allTimeGamePlayMap.get("all-time-gameplay") };
            // Set game to "All Games" since this quest tracks across all games
            allTimeQuest.game = { name: "All Games", id: null };
            allTimeQuest.gameId = null;
            processedQuestList.push(allTimeQuest);
        }


        return processedQuestList;
    }, [data?.userStats]);

    // Calculate summary statistics
    const summary = useMemo(() => {
    // Include both fully claimable quests AND quests with claimable milestones (but not already claimed)
        const claimableQuests = processedQuests.filter(q => (q.isClaimable || q.hasClaimableMilestone) && !q.isClaimed && !q.isExpired);
        // Active quests include those with progress that aren't fully complete (even if they have claimable milestones)
        const activeQuests = processedQuests.filter(q => q.progress > 0 && !q.isClaimable && !q.isClaimed && !q.isExpired);
        // ONLY use hasRecentClaims for Claimed filter - NOT isClaimed
        const claimedQuests = processedQuests.filter(q => {
            // Only check hasRecentClaims - do NOT check isClaimed
            const result = q.hasRecentClaims === true;
            return result;
        });
        // Total available XP should be from active quests (same filter as activeQuests)
        const totalAvailableXP = activeQuests
            .reduce((sum, q) => sum + q.xpReward, 0);
        const totalClaimableXP = claimableQuests
            .reduce((sum, q) => sum + q.xpReward, 0);
        const uniqueGames = new Set(processedQuests.map(q => q.gameId)).size;

        // Calculate overall progress (average of all non-claimed quest completions)
        const nonClaimedQuests = processedQuests.filter(q => !q.isClaimed);
        const overallProgress = nonClaimedQuests.length > 0
            ? Math.round(nonClaimedQuests.reduce((sum, q) => sum + q.percentComplete, 0) / nonClaimedQuests.length)
            : 0;

        return {
            claimableCount: claimableQuests.length,
            activeCount: activeQuests.length,
            claimedCount: claimedQuests.length,
            totalCount: processedQuests.length,
            totalAvailableXP,
            totalClaimableXP,
            uniqueGames,
            overallProgress,
            claimableQuests,
            activeQuests,
            claimedQuests,
        };
    }, [processedQuests]);

    return {
        quests: processedQuests,
        summary,
        isLoading,
        error,
        refetch,
        isAuthenticated: !!fullToken,
        pagination: data?.pagination,
    };
};

export default useUserQuestStats;